// RFC 7636 PKCE helpers, built on WebCrypto so a consuming SPA needs no crypto
// dependency. S256 only — Hydra enforces PKCE for public clients AS-wide, and
// `plain` would be a downgrade we have no reason to offer.

const VERIFIER_BYTES = 32

// base64url per RFC 7636 §4.1: base64 with +/ swapped for -_ and no padding.
const base64Url = (bytes: Uint8Array): string => {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export const randomUrlSafeString = (bytes = VERIFIER_BYTES): string =>
  base64Url(crypto.getRandomValues(new Uint8Array(bytes)))

export const createCodeChallenge = async (verifier: string): Promise<string> => {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier)
  )
  return base64Url(new Uint8Array(digest))
}

export interface PendingAuthorization {
  verifier: string
  state: string
  /** Where to send the user back after the redirect completes. */
  returnTo: string
}

// What the authorization server appends on the way back. Each is consumed once,
// by the callback that receives it.
const CALLBACK_PARAMS = [
  'code',
  'state',
  'error',
  'error_description',
  'error_uri',
  'iss',
  'session_state',
]

/**
 * Where to return the visitor after sign-in, taken from where they started.
 *
 * Callback parameters are dropped rather than round-tripped. Sign-in can begin
 * from a URL that still carries them — a failed exchange leaves `error` in the
 * bar, and any screen is free to use `state` for its own purposes — and a saved
 * copy outlives the redirect. Restoring one would put a spent authorization code
 * back in the address bar, into history and any logs the URL reaches, and would
 * hand the next mount something shaped exactly like a fresh callback to process.
 */
export const returnToFrom = (location: {
  pathname: string
  search: string
}): string => {
  const params = new URLSearchParams(location.search)
  for (const name of CALLBACK_PARAMS) params.delete(name)
  const query = params.toString()
  return query === '' ? location.pathname : `${location.pathname}?${query}`
}

// The verifier and state have to survive a full-page redirect to the
// authorization server, so they cannot live in memory like the access token
// does. sessionStorage is the narrowest option that survives it: scoped to this
// tab, cleared when the tab closes, and never sent to a server. The values are
// single-use and useless once the code is exchanged.
//
// One key for every first-party SPA on the origin: a redirect only ever returns
// to the app that started it (the redirect URI differs per app), so the entry is
// written and consumed by the same flow.
const PENDING_KEY = 'curri.portal.pendingAuthorization'

export const savePendingAuthorization = (pending: PendingAuthorization) => {
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending))
}

export const takePendingAuthorization = (): PendingAuthorization | null => {
  const raw = sessionStorage.getItem(PENDING_KEY)
  sessionStorage.removeItem(PENDING_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<PendingAuthorization>
    if (!parsed.verifier || !parsed.state) return null
    return {
      verifier: parsed.verifier,
      state: parsed.state,
      returnTo: parsed.returnTo ?? '/',
    }
  } catch {
    return null
  }
}

export const buildAuthorizeUrl = (options: {
  authorizeUrl: string
  clientId: string
  redirectUri: string
  scopes: string[]
  state: string
  codeChallenge: string
  /** See `AuthConfig.resourceUri` — Hydra's `audience` parameter. */
  audience?: string
}): string => {
  const url = new URL(options.authorizeUrl)
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('client_id', options.clientId)
  url.searchParams.set('redirect_uri', options.redirectUri)
  url.searchParams.set('scope', options.scopes.join(' '))
  url.searchParams.set('state', options.state)
  url.searchParams.set('code_challenge', options.codeChallenge)
  url.searchParams.set('code_challenge_method', 'S256')
  if (options.audience) url.searchParams.set('audience', options.audience)
  return url.toString()
}
